import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import axios from "axios";
import { BASEURI, headers } from "../utils/helper";
import { Cookies } from "react-cookie";

const cookies = new Cookies();

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY?.trim(),
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN?.trim(),
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID?.trim(),
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET?.trim(),
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID?.trim(),
  appId: process.env.REACT_APP_FIREBASE_APP_ID?.trim(),
};

let messaging = null;

const isSupported = 
  typeof window !== "undefined" && 
  "serviceWorker" in navigator && 
  "Notification" in window;

if (isSupported && firebaseConfig.apiKey && firebaseConfig.messagingSenderId) {
  try {
    const app = initializeApp(firebaseConfig);
    messaging = getMessaging(app);
  } catch (error) {
    console.error("Firebase App initialization failed:", error);
  }
}

/**
 * Request notification permission, register service worker, and fetch FCM token.
 */
export const requestForToken = async () => {
  if (!isSupported) {
    console.warn("Notifications or Service Workers are not supported in this browser.");
    return null;
  }

  if (!messaging) {
    console.warn("Firebase Messaging is not initialized. Verify credentials in .env.");
    return null;
  }

  if (!process.env.REACT_APP_FIREBASE_VAPID_KEY) {
    console.warn("REACT_APP_FIREBASE_VAPID_KEY is not defined in your environment. Cannot fetch FCM token.");
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      // Register the service worker cleanly
      const serviceWorkerRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
      
      // Wait for the service worker to be fully ready
      await navigator.serviceWorker.ready;
      
      const currentToken = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY?.trim(),
        serviceWorkerRegistration,
      });

      if (currentToken) {
        console.log("FCM Token successfully retrieved:", currentToken);
        // Send token to backend to update user profile if logged in
        const sessionToken = cookies.get("token");
        if (sessionToken) {
          console.log("Session token found. Updating FCM token on backend...");
          const res = await axios.post(
            `${BASEURI}/user/auth/update-fcm-token`,
            { fcmToken: currentToken },
            { headers: headers() }
          );
          console.log("Backend response for update-fcm-token:", res.data);
        } else {
          console.log("FCM token retrieved but user is not logged in yet. Storage on backend deferred.");
        }
        return currentToken;
      } else {
        console.warn("No registration token available. Request permission to generate one.");
        return null;
      }
    } else {
      console.log("Notification permission denied.");
      return null;
    }
  } catch (err) {
    console.error("Error occurred while getting FCM token:", err);
    if (err.response) {
      console.error("FCM Token API request failed with status:", err.response.status);
      console.error("Server response:", err.response.data);
    }
    return null;
  }
};

/**
 * Handle foreground messages.
 * @param {function} callback - Callback function that receives the payload.
 */
export const onMessageListener = (callback) => {
  if (!messaging) return () => {};
  return onMessage(messaging, (payload) => {
    callback(payload);
  });
};

/**
 * Tell backend to clear user's FCM token upon logout.
 */
export const clearFcmTokenOnBackend = async () => {
  try {
    await axios.post(
      `${BASEURI}/user/auth/remove-fcm-token`,
      {},
      { headers: headers() }
    );
  } catch (err) {
    console.error("Failed to clear FCM token on backend on logout:", err);
  }
};
